// neo_cogym — 프로젝트(.sb3) 저장소 (W5)
// 코딩스쿨은 .sb3 를 로컬 파일로 저장(electron will-download). 웹에선:
//   1차 = IndexedDB(오프라인/로컬), 2차 = R2(클라우드 동기, 로그인 시).
// .sb3 는 zip 바이너리 → Blob/ArrayBuffer 로 다룬다.

const DB_NAME = "neo_cogym_projects";
const STORE = "projects";

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export interface ProjectRecord {
  id: string;
  name: string;
  data: ArrayBuffer; // .sb3 zip
  updatedAt: number;
  /** R2 동기 키(클라우드 저장 시) */
  r2Key?: string;
}

export interface CloudSync {
  /** R2(서명URL/Workers)로 업로드, r2Key 반환 */
  upload(id: string, data: ArrayBuffer): Promise<string>;
  download(r2Key: string): Promise<ArrayBuffer>;
}

export const projectStore = {
  async save(id: string, name: string, data: ArrayBuffer, cloud?: CloudSync): Promise<ProjectRecord> {
    const rec: ProjectRecord = { id, name, data, updatedAt: Date.now() };
    if (cloud) {
      try {
        rec.r2Key = await cloud.upload(id, data);
      } catch {
        /* 오프라인 — 로컬만 저장, 다음 동기에서 업로드 */
      }
    }
    const db = await openDb();
    await tx(db, "readwrite", (s) => s.put(rec));
    return rec;
  },

  async load(id: string, cloud?: CloudSync): Promise<ArrayBuffer | null> {
    const db = await openDb();
    const rec = await txGet<ProjectRecord>(db, (s) => s.get(id));
    if (rec) return rec.data;
    // 로컬에 없으면 클라우드 시도(다른 기기에서 저장한 경우)
    if (cloud) {
      // 메타 조회는 별도 API 가정 — 여기선 생략(r2Key 미보유 시 null)
    }
    return null;
  },

  async list(): Promise<{ id: string; name: string; updatedAt: number }[]> {
    const db = await openDb();
    const all = await txGetAll<ProjectRecord>(db);
    return all
      .map(({ id, name, updatedAt }) => ({ id, name, updatedAt }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  },

  async remove(id: string): Promise<void> {
    const db = await openDb();
    await tx(db, "readwrite", (s) => s.delete(id));
  },
};

function tx(db: IDBDatabase, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    fn(t.objectStore(STORE));
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}
function txGet<T>(db: IDBDatabase, fn: (s: IDBObjectStore) => IDBRequest): Promise<T | null> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readonly");
    const req = fn(t.objectStore(STORE));
    req.onsuccess = () => resolve((req.result as T) ?? null);
    req.onerror = () => reject(req.error);
  });
}
function txGetAll<T>(db: IDBDatabase): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readonly");
    const req = t.objectStore(STORE).getAll();
    req.onsuccess = () => resolve((req.result as T[]) ?? []);
    req.onerror = () => reject(req.error);
  });
}
